import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, MapPin, ExternalLink, ShieldCheck, Copy, Send, RefreshCw, Ban, Mail, MailCheck, Lock, AlertTriangle, CheckCircle } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/common/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/nav";
import { SALES_STAGES, STAGE_META, PRIO_META, PLAN_META, CLAIM_META, CANONICAL_CLAIM_META, ACTIVE_CLAIM_STATUSES, CLAIM_LANGS, formatChf, stageLabel, prioLabel, planLabel, claimMetaLabel, canonicalClaimLabel } from "@/lib/sales";
import { translateApiError } from "@/lib/apiErrors";
import { ClaimEmailPreview } from "@/components/common/ClaimEmailPreview";

const TAB_KEYS = ["uebersicht", "standorte", "verkauf", "angebot", "claim", "aktivitaet"];

function Field({ label, value }) {
  const empty = value === null || value === undefined || value === "" || value === "-";
  return (
    <div>
      <dt className="text-xs text-fundio-faint">{label}</dt>
      <dd className="text-sm text-fundio-ink break-words">{empty ? <span className="italic text-fundio-faint">—</span> : String(value)}</dd>
    </div>
  );
}

export function OrgSales360({ organizationId, open, onClose, onChanged }) {
  const { can } = useAuth();
  const { t } = useTranslation();
  const canWrite = can("sales", "write");
  const [tab, setTab] = useState("uebersicht");
  const [data, setData] = useState(null);
  const [offers, setOffers] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [claimData, setClaimData] = useState(null);
  const [claimEmail, setClaimEmail] = useState("");
  const [claimLang, setClaimLang] = useState("de");
  const [lastLink, setLastLink] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [emailCfg, setEmailCfg] = useState(null);

  const load = useCallback(() => {
    if (!organizationId) return;
    setData(null);
    api.get(`/sales/organizations/${organizationId}`)
      .then((r) => {
        setData(r.data);
        const rec = r.data.record;
        setForm({
          sales_owner: rec.sales_owner || "",
          sales_notes: rec.sales_notes || "",
          next_action_at: (rec.next_action_at || "").slice(0, 10),
          last_contact_at: (rec.last_contact_at || "").slice(0, 10),
          boost_enabled: !!rec.boost_enabled,
          auto_enabled: !!rec.auto_enabled,
          sales_enabled: rec.sales_enabled !== false,
        });
      })
      .catch(() => setData({ error: true }));
  }, [organizationId]);

  useEffect(() => {
    if (open) {
      setTab("uebersicht");
      setClaimData(null); setClaimEmail(""); setClaimLang("de"); setLastLink(null); setShowPreview(false);
      load();
    }
  }, [open, load]);
  useEffect(() => { if (open && !offers) api.get("/sales/offers").then((r) => setOffers(r.data)).catch(() => {}); }, [open, offers]);

  const rec = data?.record;

  const changeStage = async (stage) => {
    setBusy(true);
    try {
      await api.patch(`/sales/organizations/${organizationId}/stage`, { sales_stage: stage });
      toast.success(t("org360.toastStage", { stage: stageLabel(stage) }));
      load(); onChanged && onChanged();
    } catch (e) { toast.error(translateApiError(t, e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/sales/organizations/${organizationId}`, {
        sales_owner: form.sales_owner,
        sales_notes: form.sales_notes,
        next_action_at: form.next_action_at || null,
        last_contact_at: form.last_contact_at || null,
        boost_enabled: form.boost_enabled,
        auto_enabled: form.auto_enabled,
        sales_enabled: form.sales_enabled,
      });
      toast.success(t("org360.toastSaved"));
      load(); onChanged && onChanged();
    } catch (e) { toast.error(translateApiError(t, e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const loadClaims = useCallback(() => {
    const tid = data?.record?.canonical_tenant_id;
    if (!tid) return;
    setClaimData(null);
    api.get(`/organizations/${tid}/claims`)
      .then((r) => setClaimData(r.data))
      .catch(() => setClaimData({ service_available: false, claims: [] }));
  }, [data]);

  useEffect(() => {
    if (open && tab === "claim" && data?.record?.canonical_tenant_id) {
      loadClaims();
      api.get("/sales/email-config").then((r) => setEmailCfg(r.data)).catch(() => setEmailCfg(null));
    }
  }, [open, tab, data, loadClaims]);

  const createClaim = async () => {
    const tid = rec.canonical_tenant_id;
    setBusy(true);
    try {
      const r = await api.post(`/organizations/${tid}/claims`, { email: claimEmail.trim(), language: claimLang, expires_in_days: 3 });
      const c = r.data.claim;
      setLastLink({ claim_url: c.claim_url, email: claimEmail.trim(), claim_id: c.claim_id, subject: c.subject, language: c.language });
      toast.success(t("org360.toastClaimCreated"));
      loadClaims();
    } catch (e) { toast.error(translateApiError(t, e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const revokeClaim = async (claimId) => {
    const tid = rec.canonical_tenant_id;
    setBusy(true);
    try {
      await api.post(`/organizations/${tid}/claims/${claimId}/revoke`);
      toast.success(t("org360.toastClaimRevoked"));
      if (lastLink?.claim_id === claimId) setLastLink(null);
      loadClaims();
    } catch (e) { toast.error(translateApiError(t, e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const testSend = async (claimId) => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/sales/claim-email/test-send", { claim_id: claimId });
      toast.success(t("org360.toastTestSent", { to: res.to }));
      loadClaims();
    } catch (e) { toast.error(translateApiError(t, e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const realSend = async (claimId) => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/sales/claim-email/send", { claim_id: claimId });
      toast.success(t("org360.toastClaimSent", { to: res.to }));
      loadClaims();
    } catch (e) { toast.error(translateApiError(t, e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const copyLink = (url) => {
    try { navigator.clipboard.writeText(url); toast.success(t("org360.toastLinkCopied")); }
    catch { toast.error(t("org360.toastCopyFail")); }
  };

  const pilotOffer = offers?.pilot_offers?.find((o) => o.key === rec?.recommended_offer);
  const realSendEnabled = !!emailCfg?.real_send_enabled;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <SheetContent data-testid="org-sales-360" className="w-full overflow-y-auto sm:max-w-xl">
        {!data ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-fundio-gold" /></div>
        ) : data.error ? (
          <p className="mt-8 text-sm text-status-danger">{t("org360.loadError")}</p>
        ) : (
          <>
            <SheetHeader>
              <SheetTitle className="flex flex-wrap items-center gap-2 font-display text-fundio-ink">
                {rec.org_name}
                <StatusBadge map={PRIO_META} value={rec.sales_priority} label={prioLabel(rec.sales_priority)} />
                <StatusBadge map={STAGE_META} value={rec.sales_stage} label={stageLabel(rec.sales_stage)} />
              </SheetTitle>
              <p className="text-xs text-fundio-muted">
                {t("org360.uniqueHeader", { count: rec.location_count, type: rec.operator_type })}
              </p>
            </SheetHeader>

            <div data-testid="org360-tabs" className="mt-4 flex flex-wrap gap-1.5">
              {TAB_KEYS.map((key) => (
                <button key={key} data-testid={`org360-tab-${key}`} onClick={() => setTab(key)}
                  className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${tab === key
                    ? "border-fundio-gold bg-fundio-gold/12 text-fundio-gold-deep"
                    : "border-fundio-border bg-white text-fundio-muted hover:border-fundio-gold"}`}>
                  {t(`org360.tabs.${key}`)}
                </button>
              ))}
            </div>

            <div className="mt-5 space-y-5">
              {tab === "uebersicht" && (
                <div className="card-surface rounded-xl p-4">
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                    <Field label={t("org360.fOrg")} value={rec.org_name} />
                    <Field label={t("org360.fOperatorType")} value={rec.operator_type} />
                    <div><dt className="text-xs text-fundio-faint">{t("org360.fSalesPriority")}</dt><dd className="mt-0.5"><StatusBadge map={PRIO_META} value={rec.sales_priority} label={prioLabel(rec.sales_priority)} /></dd></div>
                    <Field label={t("org360.fLocationCount")} value={rec.location_count} />
                    <Field label={t("org360.fCantons")} value={(rec.cantons || []).join(", ")} />
                    <div><dt className="text-xs text-fundio-faint">{t("org360.fCurrentPlan")}</dt><dd className="mt-0.5"><StatusBadge map={PLAN_META} value={rec.current_plan} label={planLabel(rec.current_plan)} /></dd></div>
                    <Field label={t("org360.fRecommendedOffer")} value={rec.recommended_offer} />
                    <Field label={t("org360.fWebsiteQuality")} value={rec.website_quality} />
                    <Field label={t("org360.fMainLocation")} value={[rec.main_location?.street, `${rec.main_location?.zip_code || ""} ${rec.main_location?.city || ""}`.trim()].filter(Boolean).join(", ")} />
                    <div><dt className="text-xs text-fundio-faint">{t("org360.fClaimStatus")}</dt><dd className="mt-0.5"><StatusBadge map={CLAIM_META} value={rec.claim_status} label={claimMetaLabel(rec.claim_status)} /></dd></div>
                  </dl>
                </div>
              )}

              {tab === "standorte" && (
                <div className="card-surface rounded-xl overflow-hidden">
                  <div className="border-b border-fundio-border px-4 py-2 text-xs font-semibold text-fundio-muted">
                    {t("org360.subLocations", { count: rec.location_count })}
                  </div>
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-fundio-border">
                      {(rec.locations || []).map((l) => (
                        <tr key={l.row_id} data-testid={`org360-loc-${l.row_id}`} className="bg-white">
                          <td className="px-4 py-2.5">
                            <div className="font-medium text-fundio-ink">{l.name}</div>
                            <div className="text-xs text-fundio-muted"><MapPin className="mr-1 inline h-3 w-3 text-fundio-faint" />{[l.street, `${l.zip_code || ""} ${l.city || ""}`.trim()].filter(Boolean).join(", ") || "—"}</div>
                          </td>
                          <td className="px-4 py-2.5 text-right"><span className="font-mono text-xs text-fundio-muted">{l.canton}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {tab === "verkauf" && (
                <div className="card-surface rounded-xl p-4 space-y-4">
                  <div>
                    <label className="text-xs text-fundio-faint">{t("org360.pipelineStage")}</label>
                    <select data-testid="org360-stage-select" disabled={!canWrite || busy} value={rec.sales_stage}
                      onChange={(e) => changeStage(e.target.value)}
                      className="mt-1 w-full rounded-lg border border-fundio-border bg-white px-3 py-2 text-sm text-fundio-ink outline-none focus:border-fundio-gold disabled:opacity-60">
                      {SALES_STAGES.map((s) => <option key={s} value={s}>{stageLabel(s)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-fundio-faint">{t("org360.salesOwner")}</label>
                    <Input data-testid="org360-owner-input" disabled={!canWrite} value={form.sales_owner}
                      onChange={(e) => setForm({ ...form, sales_owner: e.target.value })} className="input-dark mt-1" placeholder={t("org360.ownerPlaceholder")} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs text-fundio-faint">{t("org360.lastContact")}</label>
                      <Input type="date" data-testid="org360-last-contact-input" disabled={!canWrite} value={form.last_contact_at}
                        onChange={(e) => setForm({ ...form, last_contact_at: e.target.value })} className="input-dark mt-1" /></div>
                    <div><label className="text-xs text-fundio-faint">{t("org360.nextAction")}</label>
                      <Input type="date" data-testid="org360-next-action-input" disabled={!canWrite} value={form.next_action_at}
                        onChange={(e) => setForm({ ...form, next_action_at: e.target.value })} className="input-dark mt-1" /></div>
                  </div>
                  <div>
                    <label className="text-xs text-fundio-faint">{t("org360.notes")}</label>
                    <Textarea data-testid="org360-notes-input" disabled={!canWrite} value={form.sales_notes}
                      onChange={(e) => setForm({ ...form, sales_notes: e.target.value })} className="input-dark mt-1" rows={3} />
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-fundio-ink">
                    {[["boost_enabled", t("org360.boost")], ["auto_enabled", t("org360.auto")], ["sales_enabled", t("org360.salesActive")]].map(([k, lbl]) => (
                      <label key={k} className="flex items-center gap-2">
                        <input type="checkbox" data-testid={`org360-toggle-${k}`} disabled={!canWrite} checked={!!form[k]}
                          onChange={(e) => setForm({ ...form, [k]: e.target.checked })} className="h-4 w-4 accent-fundio-gold" />
                        {lbl}
                      </label>
                    ))}
                  </div>
                  {canWrite && <Button data-testid="org360-save-btn" onClick={save} disabled={busy}
                    className="bg-fundio-gold text-fundio-dark hover:bg-fundio-gold-bright">{busy ? t("common.saving") : t("common.save")}</Button>}
                </div>
              )}

              {tab === "angebot" && (
                <div className="card-surface rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-2 rounded-lg border border-status-warning-border bg-status-warning-bg/50 px-3 py-2 text-xs text-status-warning">
                    <ShieldCheck className="h-4 w-4" /> {t("org360.pilotNotLive")}
                  </div>
                  <Field label={t("org360.currentPlanTech")} value={rec.current_plan} />
                  <div><dt className="text-xs text-fundio-faint">{t("org360.recommendedPilot")}</dt>
                    <dd className="mt-1 text-sm text-fundio-ink">
                      {pilotOffer ? <><span className="font-semibold">{pilotOffer.name}</span> · {formatChf(pilotOffer.price_chf)}{t("org360.perMonth")} · {pilotOffer.includes} <span className="ml-2 rounded border border-status-warning-border bg-status-warning-bg px-1.5 py-0.5 text-[10px] font-semibold text-status-warning">{t("org360.pilotTag")}</span></> : rec.recommended_offer}
                    </dd>
                  </div>
                  {offers && (
                    <div className="mt-2 border-t border-fundio-border pt-3">
                      <p className="mb-2 text-xs font-semibold text-fundio-muted">{t("org360.allPilots")}</p>
                      <ul className="space-y-1 text-sm text-fundio-ink">
                        {offers.pilot_offers.map((o) => <li key={o.key}>{o.name} — {formatChf(o.price_chf)}{t("org360.perMonth")} · {o.includes}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {tab === "claim" && (
                <div className="space-y-4">
                  {rec.is_qa && (
                    <div data-testid="claim-qa-banner" className="flex items-start gap-2 rounded-lg border border-status-warning-border bg-status-warning-bg/60 px-3 py-2 text-xs font-semibold text-status-warning">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>{t("org360.claimQaBanner", { tenant: rec.canonical_tenant_id })}</span>
                    </div>
                  )}

                  {emailCfg && (
                    <div data-testid="email-config-status" className="rounded-lg border border-fundio-border bg-fundio-subtle px-3 py-2 text-[11px]">
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                        <span className={emailCfg.brevo_configured ? "font-semibold text-status-success" : "font-semibold text-status-danger"}>
                          Brevo: {emailCfg.brevo_configured ? "OK" : t("org360.brevoOff")}
                        </span>
                        {emailCfg.brevo_configured && <span className="text-fundio-muted">{t("org360.senderLabel")}: {emailCfg.sender_email || "—"}</span>}
                      </div>
                      {!realSendEnabled && (
                        <div className="mt-1 flex items-center gap-1 text-status-warning"><Lock className="h-3 w-3" /> {t("org360.realSendLocked")}</div>
                      )}
                    </div>
                  )}

                  {!rec.canonical_tenant_id ? (
                    <div className="card-surface rounded-xl p-4">
                      <p className="text-sm text-fundio-muted">{t("org360.claimNoCanonical")}</p>
                    </div>
                  ) : (
                    <>
                      {claimData?.writable ? (
                        <div className="card-surface rounded-xl p-4 space-y-3">
                          <div className="text-xs font-semibold uppercase tracking-wider text-fundio-muted">{t("org360.claimCreateTitle")}</div>
                          <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                            <Input data-testid="claim-email-input" type="email" value={claimEmail} disabled={busy}
                              onChange={(e) => setClaimEmail(e.target.value)} placeholder={t("org360.claimEmailPlaceholder")} className="input-dark" />
                            <select data-testid="claim-lang-select" value={claimLang} disabled={busy}
                              onChange={(e) => setClaimLang(e.target.value)}
                              className="rounded-lg border border-fundio-border bg-white px-3 py-2 text-sm text-fundio-ink outline-none focus:border-fundio-gold disabled:opacity-60">
                              {CLAIM_LANGS.map(([c, l]) => <option key={c} value={c}>{l}</option>)}
                            </select>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Button data-testid="claim-create-btn" onClick={createClaim} disabled={busy || !claimEmail.trim()}
                              className="bg-fundio-gold text-fundio-dark hover:bg-fundio-gold-bright">
                              <Send className="mr-1.5 h-4 w-4" /> {t("org360.claimCreate")}
                            </Button>
                            <Button data-testid="claim-preview-btn" variant="outline" onClick={() => setShowPreview(true)}
                              className="border-fundio-border bg-white text-fundio-ink hover:border-fundio-gold">
                              <Mail className="mr-1.5 h-4 w-4" /> {t("org360.claimPreview")}
                            </Button>
                          </div>
                          <p className="text-[11px] text-fundio-faint">{t("org360.claimPreviewOnlyNote")}</p>
                        </div>
                      ) : (
                        <div data-testid="claim-readonly-banner" className="flex items-center gap-2 rounded-lg border border-status-info-border bg-status-info-bg/50 px-3 py-2 text-xs text-status-info">
                          <ShieldCheck className="h-4 w-4 shrink-0" /> {t("org360.claimReadonlyBanner")}
                        </div>
                      )}

                      {lastLink?.claim_url && (
                        <div data-testid="claim-onetime-link" className="rounded-xl border border-fundio-gold/40 bg-fundio-gold/10 p-4 space-y-2">
                          <div className="flex items-center gap-2 text-xs font-semibold text-fundio-gold-deep">
                            <CheckCircle className="h-4 w-4" /> {t("org360.claimOneTimeTitle")}
                          </div>
                          <div className="flex items-center gap-2">
                            <code data-testid="claim-link-value" className="flex-1 overflow-x-auto whitespace-nowrap rounded-md border border-fundio-border bg-white px-2.5 py-1.5 font-mono text-[11px] text-fundio-ink">{lastLink.claim_url}</code>
                            <Button data-testid="claim-copy-btn" size="sm" onClick={() => copyLink(lastLink.claim_url)}
                              className="shrink-0 bg-fundio-gold text-fundio-dark hover:bg-fundio-gold-bright"><Copy className="h-4 w-4" /></Button>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Button data-testid="claim-preview-link-btn" variant="outline" size="sm" onClick={() => setShowPreview(true)}
                              className="border-fundio-border bg-white text-fundio-ink hover:border-fundio-gold"><Mail className="mr-1.5 h-3.5 w-3.5" /> {t("org360.claimPreviewWithLink")}</Button>
                            <Button data-testid="claim-testsend-fresh" size="sm" disabled={busy || !emailCfg?.brevo_configured} onClick={() => testSend(lastLink.claim_id)}
                              className="bg-fundio-gold text-fundio-dark hover:bg-fundio-gold-bright disabled:opacity-50"><MailCheck className="mr-1.5 h-3.5 w-3.5" /> {t("org360.testSend")}</Button>
                          </div>
                          <p className="text-[11px] text-fundio-faint">{t("org360.claimCopyNote")}</p>
                        </div>
                      )}

                      <div className="card-surface rounded-xl overflow-hidden">
                        <div className="flex items-center justify-between border-b border-fundio-border px-4 py-2">
                          <span className="text-xs font-semibold text-fundio-muted">{t("org360.claimListTitle")}</span>
                          <button data-testid="claim-refresh-btn" onClick={loadClaims} className="flex items-center gap-1 text-xs font-semibold text-fundio-gold-deep hover:underline">
                            <RefreshCw className="h-3.5 w-3.5" /> {t("common.reload")}
                          </button>
                        </div>
                        {claimData === null ? (
                          <div className="flex h-24 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-fundio-gold" /></div>
                        ) : !claimData.service_available ? (
                          <p className="p-4 text-sm text-status-danger">{claimData.reason || t("org360.claimServiceDown")}</p>
                        ) : (claimData.claims || []).length === 0 ? (
                          <p className="p-4 text-sm text-fundio-muted">{t("org360.claimNoClaims")}</p>
                        ) : (
                          <ul className="divide-y divide-fundio-border">
                            {claimData.claims.map((c) => (
                              <li key={c.claim_id} data-testid={`claim-item-${c.claim_id}`} className="bg-white px-4 py-3">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0 space-y-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <StatusBadge map={CANONICAL_CLAIM_META} value={c.status} label={canonicalClaimLabel(c.status)} />
                                      <span className="truncate font-mono text-[11px] text-fundio-faint">{c.local_recipient || c.email_masked}</span>
                                      {c.language && <span className="rounded border border-fundio-border px-1.5 text-[10px] font-semibold uppercase text-fundio-muted">{c.language}</span>}
                                      {c.test_sent_at && <span className="rounded bg-status-info-bg px-1.5 text-[10px] font-semibold text-status-info">{t("org360.testSentBadge")}</span>}
                                      {c.sent_at && <span className="rounded bg-status-success-bg px-1.5 text-[10px] font-semibold text-status-success">{t("org360.sentBadge")}</span>}
                                    </div>
                                    {c.subject && <div className="truncate text-xs text-fundio-ink"><span className="text-fundio-faint">{t("org360.subjectLabel")}:</span> {c.subject}</div>}
                                    <div className="text-[11px] text-fundio-muted">
                                      {t("org360.claimCreated", { date: formatDate(c.created_at) })} · {t("org360.claimValidUntil", { date: formatDate(c.expires_at) })}
                                      {c.opened_at && <> · {t("org360.claimOpened", { date: formatDate(c.opened_at) })}</>}
                                      {c.claimed_at && <> · {t("org360.claimClaimed", { date: formatDate(c.claimed_at) })}</>}
                                    </div>
                                    {c.claim_url && <div className="truncate text-[11px]"><span className="text-fundio-faint">{t("org360.ctaUrlLabel")}:</span> <span className="font-mono text-fundio-gold-deep">{c.claim_url}</span></div>}
                                  </div>
                                  {claimData.writable && (
                                    <div className="flex shrink-0 flex-col items-end gap-1.5">
                                      {c.has_local && (
                                        <div className="flex gap-1.5">
                                          <Button data-testid={`claim-testsend-${c.claim_id}`} size="sm" variant="outline" disabled={busy || !emailCfg?.brevo_configured}
                                            onClick={() => testSend(c.claim_id)} className="border-fundio-border bg-white text-fundio-ink hover:border-fundio-gold disabled:opacity-50">
                                            <MailCheck className="mr-1 h-3.5 w-3.5" /> {t("org360.testSend")}
                                          </Button>
                                          <Button data-testid={`claim-send-${c.claim_id}`} size="sm" disabled={busy || !realSendEnabled || !c.test_sent_at}
                                            onClick={() => realSend(c.claim_id)}
                                            title={!realSendEnabled ? t("org360.sendLockedHint") : ""}
                                            className="bg-fundio-gold text-fundio-dark hover:bg-fundio-gold-bright disabled:opacity-50">
                                            {realSendEnabled ? <Send className="mr-1 h-3.5 w-3.5" /> : <Lock className="mr-1 h-3.5 w-3.5" />} {t("org360.claimSend")}
                                          </Button>
                                        </div>
                                      )}
                                      {ACTIVE_CLAIM_STATUSES.includes(c.status) && (
                                        <Button data-testid={`claim-revoke-${c.claim_id}`} size="sm" variant="outline" disabled={busy}
                                          onClick={() => revokeClaim(c.claim_id)}
                                          className="border-status-danger-border bg-white text-status-danger hover:bg-status-danger-bg">
                                          <Ban className="mr-1 h-3.5 w-3.5" /> {t("org360.claimRevoke")}
                                        </Button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </>
                  )}

                  <ClaimEmailPreview open={showPreview} onClose={() => setShowPreview(false)}
                    orgName={rec.org_name} recipient={lastLink?.email || claimEmail} claimUrl={lastLink?.claim_url || null} />
                </div>
              )}

              {tab === "aktivitaet" && (
                <div className="card-surface rounded-xl p-4">
                  {(data.activity || []).length === 0 ? <p className="text-sm text-fundio-muted">{t("org360.noActivity")}</p> : (
                    <ul className="space-y-2">
                      {data.activity.map((a, i) => (
                        <li key={i} data-testid="org360-activity-item" className="border-b border-fundio-border pb-2 text-xs last:border-0">
                          <div className="font-medium text-fundio-ink">{a.action}</div>
                          <div className="text-fundio-muted">{a.detail}</div>
                          <div className="text-fundio-faint">{a.user_email} · {formatDate(a.timestamp)}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
